import TextField from "@material-ui/core/TextField";
import EditIcon from "@material-ui/icons/Edit";
import dayjs from "dayjs";
import _ from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import Accordion from "../../components/Accordion";
import AlertDialog from "../../components/AlertDialog";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import CheckpointScoreGrid from "../../components/CheckpointScoreGrid";
import DatePickerGroup from "../../components/DatePickerGroup";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import ReviewDateSummary from "../../components/ReviewDateSummary";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import SessionDeleteButton from "../../components/SessionDeleteButton";
import SessionSummary from "../../components/SessionSummary";
import Stepper from "../../components/Stepper";
import { SessionStatus } from "../../config/Config";
import { sessionPath } from "../../config/GetPagePath";
import {
  IsCampaignAdmin,
  IsCampaignManager,
  IsSessionCoach,
  IsSessionCoachee,
  IsSessionReadOnly
} from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import GetSessionData from "../../services/getSessionData";
import GetReportingManagerForSession from "../../services/getSessionReportingManager";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { specialCharacterCheck } from "../../utils/customValidation";
import { dayJS, localToUTC, utcToLocal } from "../../utils/datetime";
import styles from "./SessionsUpdateCheckpoints.module.scss";
const emptyFinalScoreTemplate = {
  subject_id: 0,
  review_progress_score: undefined,
  action_at_checkpoint: null,
};

export function SessionsUpdateCheckpoints() {
  const history = useHistory();
  const { addNotification } = useNotification();
  const { id } = useParams();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [sessionDuration, setSessionDuration] = useState(0);
  const [reportingManager, setReportingManager] = useState(null);
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [isProgressing, setIsProgressing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [finalData, setFinalData] = useState([]);
  const [finalScores, setFinalScores] = useState([]);
  const [dateOverride, setDateOverride] = useState(false);
  const [postPoneReviewDate, setPostPoneReviewDate] = useState(new Date());
  const [minReviewDate, setMinReviewDate] = useState(new Date());
  const [maxReviewDate, setMaxReviewDate] = useState(new Date());
  const [openPostpone, setOpenPostpone] = useState(false);
  const [postponeReason, setPostponeReason] = useState("");
  const [previousReviewDate, setPreviousReviewDate] = useState([]);
  const [isPostponeReasonEmpty, setIsPostponeReasonEmpty] = useState(false);
  const [specialCharacterError, setSpecialCharacterError] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const [isFilledAndValid, setIsFilledAndValid] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);

  let localData = {};
  if (localStorage.sessionCheckPointData) {
    localData = localStorage.sessionCheckPointData;
  }

  useEffect(() => {
    setTimeout(() => {
      if (localData && localData.checkpoints !== undefined) {
        setCheckpoints(localData.checkpoints);
      }
      if (localData && localData.finalScores !== undefined) {
        setFinalScores(localData.finalScores);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (localData && localData.checkpoints !== undefined) {
        setCheckpoints(localData.checkpoints);
      }
      if (localData && localData.finalScores !== undefined) {
        setFinalScores(localData.finalScores);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    // get session detail
    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      const reportingManagerAPIData = await GetReportingManagerForSession(id);
      if (sessionAPIData) {
        setSessionDetails(sessionAPIData);
      } else {
        addNotification(
          `Could not get session details from id #${id}`,
          "error"
        );
      }
      if (reportingManagerAPIData) {
        setReportingManager(reportingManagerAPIData);
      }
      setIsLoading(false);
    }

    fetchSessionData();

    // get Review Date details
    apiRequest
      .get(`/api/getSessionReviewDate?session_id=${id}`)
      .then(response => {
        const data = response.data.data;
        setPreviousReviewDate(data.previous_review_date);
      })
      .catch(error => {
        addNotification(t("error.errorGettingReviewDateSummary"), "error");
        console.error(error);
      });

    // get checkpoint details
    apiRequest
      .get(`/api/getCheckpointDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;

        if (data && data.progress_data) {
          setCheckpoints(data.progress_data);
        }

        if (data && data.final_data) {
          setFinalScores(data.final_data);
          setFinalData(data.final_data);
        }
        // hacky: if there is no 'final_data' array to be found, it means that the review scores
        // hasn't been submitted yet. pre-fill an array of `finalScores` for each subject_id using the emptytemplate.
        else if (data && data.progress_data && !data.final_data) {
          let newFinalScores = [];
          let subjectIds = [];
          data.progress_data.forEach(cp => {
            if (!subjectIds.includes(cp.subject_id)) {
              subjectIds.push(cp.subject_id);
            }
          });

          subjectIds.forEach(id => {
            let finalObj = _.cloneDeep(emptyFinalScoreTemplate);
            finalObj.subject_id = id;
            newFinalScores.push(finalObj);
          });
          setFinalScores(newFinalScores);
        }
        // hack end
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (!_.isEmpty(sessionDetails)) {
      const originalReviewDate = sessionDetails.review_date_history.length > 0 ? sessionDetails.review_date_history[0].previous_review_date : getSubjectValue("final_date")
      const currentReviewDate = utcToLocal(
        originalReviewDate
      ).format("DD MMM YYYY h:mm A");

      const lastCheckpointDate = checkpoints.reduce(
        (latest, { checkpoint_date }) =>
          checkpoint_date > latest ? checkpoint_date : latest,
        ""
      );

      let setNewReviewDate = dayjs(lastCheckpointDate).add(1, "day").toDate();
      const today = new Date();
      if (setNewReviewDate < today) {
        setNewReviewDate = today;
      }

      setPostPoneReviewDate(setNewReviewDate);
      setMinReviewDate(setNewReviewDate);
      setMaxReviewDate(
        dayjs(currentReviewDate).add(1, "month").endOf("month").toDate()
      );
    }
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
    setIsProgressing(getSessionValue("status") === SessionStatus.PROGRESSING);
  }, [sessionDetails]);

  const isReadOnly = useMemo(() => {
    if (_.isEmpty(sessionDetails) || !sessionDetails.session) return true;
    return IsSessionReadOnly(
      sessionDetails.session.coach_id,
      sessionDetails.session.coachee_id
    );
  }, [sessionDetails]);

  // helper function to access values within session details
  const getSessionValue = key => {
    return sessionDetails.session ? sessionDetails.session[key] : undefined;
  };
  // helper function to access values within session details
  const getSubjectValue = key => {
    return sessionDetails.subject && sessionDetails.subject[0]
      ? sessionDetails.subject[0][key]
      : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  // check for checkpoint grid completion
  useEffect(() => {
    let complete = true;

    const checkPointDate = (date) => {
      return new Date(
        utcToLocal(date).format("DD MMM YYYY")
      ).setHours(0, 0, 0, 0)
    };
    const today = new Date().setHours(0, 0, 0, 0);

    checkpoints.forEach(o => {
      if (!!!o.checkpoint_progress_value) {
        complete = false;
      }

      if (checkPointDate(o.checkpoint_date) > today) {
        setIsEditable(true);
      }
    });
    finalScores.forEach(o => {
      if (!!!o.review_progress_score) {
        complete = false;
      }
      if (_.isEmpty(o.review_progress_score)) {
        complete = false;
      }
      if (!dateOverride){
        if (checkPointDate(o.review_datetime) > today) {
          complete = false;
        }
      }
    });

    setComplete(complete);
  }, [checkpoints, finalScores, dateOverride]);

  const handleConfirm = (type, isFinal = false) => {
    setIsLoading(true);
    const paramObj = {
      session_id: parseInt(id),
      duration: sessionDuration,
      created_by: sessionDetails.session.coach_id,
      progress_data: checkpoints.map(item => {
        return {
          checkpoint: dayJS(item.checkpoint_date).format("YYYY-MM-DD"),
          subject_id: item.subject_id,
          checkpoint_progress_value: item.checkpoint_progress_value,
          action_at_checkpoint: item.action_at_checkpoint,
        };
      }),
      final_data: finalScores,
      campaign_manager: IsCampaignAdmin(),
    };

    const today = dayJS();
    const listOfBlankCheckPoint = [];
    const listOfMandatoryCheckPoint = [];

    for (const input of paramObj.progress_data) {
      if (!today.isBefore(input.checkpoint)) {
        listOfMandatoryCheckPoint.push(input);
      }

      if (!input.checkpoint_progress_value) {
        listOfBlankCheckPoint.push(input);
      }
    }
    const listOfMandatoryCheckpointNotGiven = listOfMandatoryCheckPoint.filter(
      input => !input.checkpoint_progress_value
    );
    const isAllCheckPointsBlank =
      listOfBlankCheckPoint.length === paramObj.progress_data.length;

    if (listOfMandatoryCheckpointNotGiven.length > 0 || isAllCheckPointsBlank) {
      addNotification(
        `Please fill in all the required checkpoint(s) when it's editable`,
        "error"
      );
      setIsLoading(false);
      return;
    }
    apiRequest
      .post(`/api/saveCheckpointProgressScore`, paramObj)
      .then(resp => {
        if (resp.status === 200) {
          addNotification(
            t("error.checkpointProgressScoreHasBeenSavedSuccessfully"),
            "success"
          );

          localStorage.removeItem("sessionCheckPointData");

          if (complete && type !== "postpone") {
            setTimeout(() => {
              setIsLoading(false);
              history.push(`/sessions/review-session/${id}`);
            }, 2000);
          } else {
            setTimeout(() => {
              setIsLoading(false);
              history.push(sessionPath());
            }, 2000);
          }
        } else {
          setIsLoading(false);
          addNotification(t(`${resp.data.error}`), "error");
        }
      })
      .catch(error => {
        addNotification(`${error}`, "error");
        setIsLoading(false);
      });
  };

  const handleDateChange = date => {
    setPostPoneReviewDate(date);
  };

  const handlePostPoneReview = () => {
    const new_review_date = localToUTC(postPoneReviewDate).format(
      "YYYY-MM-DD HH:mm:ss"
    );
    const previous_review_date = getSessionValue("review_datetime");

    const trimmed = postponeReason.trim();

    if (trimmed === "") {
      setIsPostponeReasonEmpty(true);
      return;
    }

    if (!specialCharacterCheck(postponeReason)) {
      setSpecialCharacterError(true);
      return;
    }

    const formObj = {
      session_id: parseInt(id),
      new_review_date: new_review_date,
      previous_review_date: previous_review_date,
      reason: postponeReason,
      created_by: sessionDetails.session.coach_id,
    };

    apiRequest
      .post(`/api/postponeReviewDate`, formObj)
      .then(resp => {
        if (resp.status === 200) {
          const data = resp.data.data;
          setOpenPostpone(false);

          if (
            _.isEmpty(finalData[0].review_progress_score) ||
            finalData[0].review_progress_score === "0"
          ) {
            handleConfirm("postpone");
          } else {
            addNotification(`${data}`, "success");
            setTimeout(() => {
              history.push(sessionPath());
            }, 2000);
          }
        }
      })
      .catch(error => {
        addNotification(`${error}`, "error");
        setOpenPostpone(false);
      })
      .finally(() => { });
  };

  return (
    <DashboardLayout
      appBarContent={
        <Stepper
          phase={t("header.stepper.check")}
          phaseLabel="Check"
          sessionId={id}
          clockRun={!isReadOnly}
          getTotalTimeSpent={getTotalTime}
        />
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionDetails["coach_id"] === parseInt(getLocalValue("uid"))
          }
          accessible={!isReadOnly}
        />
      }
      contentClass={styles.dashboardLayout__content}
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <div className={styles.detailsContainer}>
              <BackButton text={t("common.backToSession")} />
              <SectionTitle
                append={
                  <>
                    {getLocalValue("employee_id") === reportingManager &&
                      IsCampaignManager() && (
                        <SessionDeleteButton sessionId={id} />
                      )}
                    {isEditable && (
                      <div
                        className={styles.sectionTitleEditBtn}
                        onClick={() => {
                          history.push(`/sessions/edit-checkpoints/${id}`);
                        }}
                      >
                        <EditIcon /> {t("updateCheckpoint.edit")}
                      </div>
                    )}
                  </>
                }
              >
                {t("updateCheckpoint.updateCheckpoint")}
              </SectionTitle>
              <div className={styles.sessionDetailsBlock}>
                <SessionBasicInfo sessionData={sessionDetails.session} />
                <div>
                  <Accordion
                    summary={t("updateCheckpoint.viewSessionSummary")}
                    details={<SessionSummary id={id}></SessionSummary>}
                  />

                  <ReviewDateSummary id={id} />
                </div>
              </div>
              <div className={styles.sessionUpdateCheckpointScoresBlock}>
                <CheckpointScoreGrid
                  sessionID={id}
                  checkpoints={checkpoints}
                  setCheckpoints={setCheckpoints}
                  finalScores={finalScores}
                  setFinalScores={setFinalScores}
                  reviewDate={getSubjectValue("final_date")}
                  dateOverride={dateOverride}
                  setIsFilledAndValid={setIsFilledAndValid}
                  disabled={isReadOnly}
                />
              </div>
              {sessionCoach && isProgressing && (
                <div className={styles.confirmButtonWrapper}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    isloading={isLoading ? 1 : 0}
                    onClick={handleConfirm}
                  >
                    {complete
                      ? t("updateCheckpoint.startReviewSession")
                      : t("updateCheckpoint.saveProgress")}
                  </Button>

                  {complete && (
                    <Button
                      variant="outlined"
                      color="primary"
                      isdisabled={previousReviewDate.length > 2 ? 1 : 0}
                      fullWidth
                      onClick={() => {
                        setOpenPostpone(true);
                      }}
                      style={{ marginTop: "8px" }}
                    >
                      {t("updateCheckpoint.rescheduleReviewSession")}
                    </Button>
                  )}
                </div>
              )}

              <div
                style={{
                  marginTop: "64px",
                  display: `${process.env.REACT_APP_COACHING_WEB_DEBUGGER === "true"
                    ? "block"
                    : "none"
                    } `,
                }}
                data-value={process.env.NODE_ENV}
                data-debug={process.env.REACT_APP_COACHING_WEB_DEBUGGER}
              >
                dev/testing tool:
                <br />
                <button
                  onClick={() => {
                    setDateOverride(!dateOverride);
                  }}
                >
                  toggle date override: {dateOverride ? "ON" : "OFF"}
                </button>
              </div>
            </div>
          </Section>
        </PageContent>
      </div>
      <AlertDialog
        maxWidth="xs"
        open={openPostpone}
        onClose={() => {
          setOpenPostpone(false);
        }}
        title={t("updateCheckpoint.rescheduleReviewSessionTo")}
        body={
          <Fragment>
            <div className={"margin-bottom-16"}>
              <DatePickerGroup
                disablePast
                minDate={minReviewDate}
                maxDate={maxReviewDate}
                selectedDate={postPoneReviewDate}
                handleDateChange={handleDateChange}
              />
            </div>
            <TextField
              className={styles.textarea}
              multiline
              rows={4}
              inputProps={{ style: { resize: "both" } }}
              fullWidth
              placeholder={t("updateCheckpoint.pleaseStateReason")}
              variant="outlined"
              onChange={e => {
                const val = e.target.value;
                setPostponeReason(e.target.value);
                setIsPostponeReasonEmpty(val ? false : true);
              }}
              error={isPostponeReasonEmpty || specialCharacterError}
              helperText={
                isPostponeReasonEmpty
                  ? "Please add the postpone reason"
                  : specialCharacterError &&
                  "Special characters are not allowed"
              }
            />
          </Fragment>
        }
        footer={
          <Fragment>
            <Button
              size="small"
              onClick={() => {
                setOpenPostpone(false);
              }}
            >
              {t("updateCheckpoint.cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handlePostPoneReview}
              variant="contained"
              size="small"
            >
              {t("updateCheckpoint.schedule")}
            </Button>
          </Fragment>
        }
      />
    </DashboardLayout>
  );
}
